<template>
  <CancelFlowRedesignExperiment>
    <template #default>
      <div class="row">
        <PlainButton
          v-for="plan in plans"
          :key="plan.id"
          :disabled="disabled || plan.id === currentPlanId"
          class="col py-2 px-lg-1"
          @click="onPlanSelected(plan.id)">
          <PlanCard
            :plan="plan"
            :selected="plan.id === selectedPlanId"
            :header-text="plan.id === currentPlanId ? 'Current Plan' : (!plan.prepayment && plan.shipmentsPerInterval === null) ? 'Best Value' : ''"/>
        </PlainButton>
      </div>
    </template>
    <template #redesign>
      <div class="plan-selector-wrapper">
        <div class="row">
          <div
            v-for="plan in plans"
            :key="plan.id"
            class="col-12 col-lg-4 mb-md">
            <AdornPlanCard 
              :plan-id="plan.id" 
              :tag-line="plan.id === currentPlanId ? 'Current Plan' : (!plan.prepayment && plan.shipmentsPerInterval === null) ? 'Best Value' : ''"
              :force-selected="selectedPlanFromProps === plan.id"
              @selected="onPlanSelected(plan.id)"/>
          </div>
        </div>
        <CalloutSection
          v-if="membershipPlans && !shouldUseTabletLayout && selectedHeroPlan && computedVisiblePaymentOptions.length && selectedPlanFromProps"
          :key="`callout-${selectedHeroPlan}`"
          :position="computedCalloutPosition">
          <template #default>
            <FadeTransition>
              <div
                :key="`payment-options-${selectedHeroPlan}`"
                class="d-flex flex-column align-items-center">
                <h5>Choose your payment option</h5>
                <div class="payment-options-wrapper d-flex flex-wrap gap-md">
                  <PlanCardPaymentOption
                    v-for="paymentOption in computedVisiblePaymentOptions"
                    :key="paymentOption.id"
                    :plan="paymentOption"
                    @selected="onPaymentOptionSelected(paymentOption.id)"/>
                </div>
              </div>
            </FadeTransition>
          </template>
        </CalloutSection>
        <div
          v-if="shouldUseTabletLayout && selectedHeroPlan && computedVisiblePaymentOptions.length"
          :key="`mobile-payment-options-${selectedHeroPlan}`"
          class="payment-options-mobile mt-md">
          <h5 class="text-center mb-md">
            Choose your payment option
          </h5>
          <div class="d-flex flex-column gap-md">
            <PlanCardPaymentOption
              v-for="paymentOption in computedVisiblePaymentOptions"
              :key="paymentOption.id"
              :plan="paymentOption"
              @selected="onPaymentOptionSelected(paymentOption.id)"/>
          </div>
        </div>
      </div>
    </template>
  </CancelFlowRedesignExperiment>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import PlanCard from '../subscribe/PlanCard'
import AdornPlanCard from '@shared/components/ADORN/planCard/PlanCard.vue'
import CancelFlowRedesignExperiment from '@/components/account/membership/new/CancelFlowRedesignExperiment.vue'
import PlainButton from '../global/sequin/PlainButton'
import CalloutSection from '@shared/components/ADORN/CalloutSection.vue'
import FadeTransition from '@shared/components/ADORN/transitions/FadeTransition.vue'
import PlanCardPaymentOption from '@shared/components/ADORN/planCard/PlanCardPaymentOption.vue'
import useAnalytics from '@shared/composables/analytics.js'
import { usePlanSelection } from '@shared/composables/planSelection.js'
import { usePlans } from '@shared/composables/plans.js'

const props = defineProps({
  header: {
    type: String,
    required: true
  },
  plans: {
    type: Array,
    required: true
  },
  selectedPlanId: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,    default: false
  },
  currentPlanId: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['plan-selected'])

const { track } = useAnalytics()
const { initializeMembershipPlans } = usePlans()
const {
  selectedHeroPlan,
  membershipPlans,
  onPlanSelected: selectPlan,
  onPaymentOptionSelected: selectPaymentOption,
  shouldUseTabletLayout,
  selectMembershipPlan,
  paymentOptionsByNumItems
} = usePlanSelection()

const selectedPlanFromProps = ref(null)

const computedVisiblePaymentOptions = computed(() => {
  if (!selectedHeroPlan.value || !membershipPlans.value) return []
  const heroPlan = membershipPlans.value.find(p => p.id === selectedHeroPlan.value)
  if (!heroPlan) return []
  return paymentOptionsByNumItems(heroPlan.nItems) || []
})

function findHeroPlan(plan) {
  if (!membershipPlans.value) return null
  const planGroup = membershipPlans.value.filter(p => p.nItems === plan.nItems)
  return planGroup.find(p => p.chargeInterval === 1) || planGroup[0]
}

function getPlanCardOrder(planId) {
  const index = props.plans.findIndex(p => p.id === planId)
  if (index === -1) return 1 // default to center
  
  const totalPlans = props.plans.length
  if (totalPlans === 1) return 1 // center
  if (totalPlans === 2) return index === 0 ? 0 : 2 // start or end
  if (index === 0) return 0 // start
  if (index === totalPlans - 1) return 2 // end
  return 1 // center (or middle positions)
}

const computedCalloutPosition = computed(() => {
  if (!selectedPlanFromProps.value) return 'center'
  const order = getPlanCardOrder(selectedPlanFromProps.value)
  if (order === 0) return 'start'
  if (order === 1) return 'center'
  if (order === 2) return 'end'
  return 'center'
})

async function onPlanSelected(planId) {
  const plan = props.plans.find(p => p.id === planId)
  if (!plan) {
    emit('plan-selected', planId)
    return
  }
  
  selectedPlanFromProps.value = planId
  
  const heroPlan = findHeroPlan(plan)
  if (heroPlan) {
    selectedHeroPlan.value = heroPlan.id
    selectPlan(heroPlan.id)
    await selectMembershipPlan({ planId: heroPlan.id, trackEvent: true })
  }
  
  emit('plan-selected', planId)
}

async function onPaymentOptionSelected(paymentOptionId) {
  await selectPaymentOption(paymentOptionId)
  emit('plan-selected', paymentOptionId)
}

onMounted(async () => {
  await initializeMembershipPlans()
  
  if (props.selectedPlanId) {
    let selectedPlan = props.plans.find(p => p.id === props.selectedPlanId)
    let isPaymentOption = false
    
    if (!selectedPlan && membershipPlans.value) {
      const selectedPaymentOption = membershipPlans.value.find(p => p.id === props.selectedPlanId)
      if (selectedPaymentOption) {
        isPaymentOption = true
        selectedPlan = props.plans.find(p => {
          const heroPlan = findHeroPlan(p)
          return heroPlan && heroPlan.nItems === selectedPaymentOption.nItems
        })
        if (selectedPlan) {
          selectedPlanFromProps.value = selectedPlan.id
        }
      }
    } else if (selectedPlan) {
      selectedPlanFromProps.value = props.selectedPlanId
    }
    
    if (selectedPlan) {
      const heroPlan = findHeroPlan(selectedPlan)
      if (heroPlan) {
        selectedHeroPlan.value = heroPlan.id
        selectPlan(heroPlan.id)
        const planIdToSelect = isPaymentOption ? props.selectedPlanId : heroPlan.id
        await selectMembershipPlan({ planId: planIdToSelect, trackEvent: false })
      }
    }
  }
  
  track('Viewed Plan Selector', {
    source: props.header,
    plans: props.plans.map(p => p.displayName)
  })
})
</script>

<style scoped lang="scss">
.plan-selector-wrapper {
  position: relative;
  padding-bottom: 80px;
}

.payment-options-wrapper {
  @media screen and (max-width: 1314px) {
    max-width: 500px;
  }
}

.payment-options-mobile {
  padding: 0 16px;
}
</style>
